import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Download, Plane, Plus, Suitcase } from '@factorialco/f0-react/icons/app'
import { MyGroupedExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { mapFiltersToVariables } from 'modules/expenses/components/expenses/DataCollection/filters/filterUtils'
import { useDataCollectionConfig } from 'modules/expenses/components/expenses/DataCollection/useDataCollectionConfig'
import { useExpensablesStatusCounts } from 'modules/expenses/components/expenses/DataCollection/useExpensablesStatusCounts'
import { useExpensesDataCollection } from 'modules/expenses/components/expenses/DataCollection/useExpensesDataCollection'
import { useMyExpensesBulkActions } from 'modules/expenses/components/my_expenses/DataCollection/useMyExpensesBulkActions'
import { useMyExpensesFilters } from 'modules/expenses/components/my_expenses/DataCollection/useMyExpensesFilters'
import { useMyExpensesItemActions } from 'modules/expenses/components/my_expenses/DataCollection/useMyExpensesItemActions'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { Group, useExportPdf } from 'modules/expenses/hooks/useGroup'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isDraftGroup } from 'modules/expenses/lib/groups/group'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'

interface Props {
  group: Group
  companyId: number
  canSeeTransactions: boolean
  hasReportOnBehalfFeature: boolean
}

export const MyGroupedExpensesDataCollection = ({
  group,
  companyId,
  canSeeTransactions,
  hasReportOnBehalfFeature,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const {
    employee,
    access: { locale },
  } = useCurrentSession()
  const { featureAvailabilities, data: myExpensesData } = useMyExpenses()
  const { exportPdf, loading: exportingPdf } = useExportPdf()

  const canCreateExpensesForOthers =
    myExpensesData?.expenses?._policies?.expenseCreateExpensesForOthers?.can ?? false
  const filterDefinitions = useMyExpensesFilters({
    companyId,
    hasReportOnBehalfFeature,
    canCreateExpensesForOthers,
    withinGroup: true,
  })

  const statusCounts = useExpensablesStatusCounts({
    companyId,
    groupId: group.id,
    includeManualDrafts: true,
  })

  const { navigationFilters: navigationFiltersDefinitions, presets: presetsDefinitions } =
    useDataCollectionConfig({ includeDraft: true, statusCounts, showDateNavigation: false })

  const { sortings, defaultSortings, columns } = useExpensesDataCollection({
    isMyExpenses: true,
    locale,
    showGroupColumn: false,
  })

  const { bulkActions, handleBulkAction, handlers, Modals } = useMyExpensesBulkActions({
    expensableGroupsEnabled: context.expensableGroupsEnabled,
    employeeId: employee?.id ?? 0,
  })

  const { getItemActions } = useMyExpensesItemActions({
    expensableGroupsEnabled: context.expensableGroupsEnabled && isDraftGroup(group),
    employeeId: employee?.id ?? 0,
    handlers,
    navigationHandler: { navigate, navigation },
    isViewingGroup: true,
  })

  const handleExportPDF = async () => {
    const handled = handleResult(await exportPdf(group?.id, companyId), () => null)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.error'),
      })
    }
  }

  const dataSource = useGraphqlDataCollectionSource({
    document: MyGroupedExpensablesDataCollectionDocument,
    mapData: (data) => data.expenses.expensablesConnection.nodes,

    mapVariables: ({ search, sortings: mapSortings, filters }) => {
      const { status, employeeIds, filter } = mapFiltersToVariables(filters)

      return {
        companyId: companyId.toString(),
        sortOrder: mapSortings?.[0],
        includeManualDrafts: true,
        search,
        groupIds: [group.id],
        status,
        employeeIds,
        filter,
      }
    },

    paginationType: 'infinite-scroll',
    perPage: 100,
    mapPagination: (data) => ({
      total: data.expenses.expensablesConnection.totalCount ?? 0,
      cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
    }),

    navigationFilters: navigationFiltersDefinitions,
    search: {
      enabled: true,
    },
    filters: filterDefinitions,
    presets: presetsDefinitions,
    sortings,
    defaultSortings,
    itemUrl: ({ id }) => navigation.helpers.expensableDetail(id),
    itemActions: getItemActions,
    selectable: (item) => item.id,
    bulkActions,
    primaryActions: () => {
      if (!featureAvailabilities.canCreateExpenses || !isDraftGroup(group)) {
        return undefined
      }

      const actions = []

      // Manual expenses and mileage are always available if canCreateExpenses is true
      actions.push({
        label: i18n.t('expenses_v2.buttons.add_expense'),
        icon: Plus,
        onClick: () => navigate(navigation.helpers.newExpenseOcr()),
      })

      actions.push({
        label: i18n.t('expenses_v2.buttons.add_mileage'),
        icon: Plane,
        onClick: () => navigate(navigation.helpers.newMileage()),
      })

      // Per diems are only available if the feature is enabled
      if (featureAvailabilities.canUsePerDiems) {
        actions.push({
          label: i18n.t('expenses_v2.buttons.add_per_diem'),
          icon: Suitcase,
          onClick: () => navigate(navigation.helpers.newPerDiem()),
        })
      }

      return actions.length > 0 ? actions : undefined
    },
    secondaryActions: {
      expanded: 0,
      actions: () => [
        {
          label: i18n.t('expenses_v2.buttons.export_to_pdf'),
          icon: Download,
          onClick: () => {
            !exportingPdf && handleExportPDF()
          },
        },
      ],
    },
  })

  return (
    <>
      <Modals />
      <Outlet context={{ ...context, canSeeTransactions, group, dataCollectionEnabled: true }} />
      <Box paddingX="s12" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <OneDataCollection
          id={`my-expenses/group-${group.id}/v1`}
          storage={{
            features: ['filters', 'sortings'],
          }}
          fullHeight
          source={dataSource}
          emptyStates={{
            'no-data': {
              title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
              description: '',
              actions:
                featureAvailabilities.canCreateExpenses && isDraftGroup(group)
                  ? [
                      {
                        label: i18n.t('expenses_v2.buttons.add_expense'),
                        icon: Plus,
                        onClick: () => navigate(navigation.helpers.newExpenseOcr()),
                      },
                    ]
                  : undefined,
            },
          }}
          onBulkAction={handleBulkAction}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
        />
      </Box>
    </>
  )
}
